import { getCloudConfig } from './config';

export class CloudApiError extends Error {
  constructor(message, { status = 0, details = null } = {}) {
    super(message);
    this.name = 'CloudApiError';
    this.status = status;
    this.details = details;
  }
}

function config() {
  return getCloudConfig();
}

function endpoint(base, path) {
  const suffix = String(path || '');
  if (/^https?:\/\//i.test(suffix)) return suffix;
  return `${base}${suffix.startsWith('/') ? suffix : `/${suffix}`}`;
}

async function parseResponse(response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return response.json();
  const text = await response.text();
  return text ? { message: text } : null;
}

function errorMessage(payload, fallback) {
  return payload?.error?.message || payload?.message || payload?.error || fallback;
}

async function request(base, path, options = {}) {
  const headers = new Headers(options.headers || {});
  if (options.body != null && !headers.has('Content-Type') && options.json !== false) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(endpoint(base, path), {
    ...options,
    headers,
    credentials: options.credentials || 'include',
    body: options.body == null || options.json === false ? options.body : JSON.stringify(options.body),
  });
  const payload = await parseResponse(response);
  if (!response.ok) {
    throw new CloudApiError(errorMessage(payload, `请求失败（${response.status}）`), {
      status: response.status,
      details: payload?.details || payload?.error?.details || null,
    });
  }
  return payload;
}

function entity(payload, key) {
  return payload?.[key] || payload;
}

function projectPayload(payload) {
  const project = entity(payload, 'project');
  if (!payload?.project) return project;
  return {
    ...project,
    document: project.document || payload.document || payload.draft,
    assets: project.assets || payload.assets,
    version: project.version || payload.version,
  };
}

function collection(payload, key) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.[key]) ? payload[key] : [];
}

export function apiPath(path) {
  return endpoint(config().apiBase, path);
}

export function publicApiPath(path) {
  return endpoint(config().publicApiBase, path);
}

export const cloudApi = {
  async bootstrapStatus() {
    return request(config().apiBase, '/auth/bootstrap-status');
  },

  async currentUser() {
    try {
      return entity(await request(config().apiBase, '/auth/me'), 'user');
    } catch (error) {
      if (error instanceof CloudApiError && error.status === 401) return null;
      throw error;
    }
  },

  async bootstrap(input, bootstrapToken = '') {
    const headers = bootstrapToken ? { 'x-vr-bootstrap-token': bootstrapToken } : undefined;
    return entity(await request(config().apiBase, '/auth/bootstrap', { method: 'POST', body: input, headers }), 'user');
  },

  async login(input) {
    return entity(await request(config().apiBase, '/auth/login', { method: 'POST', body: input }), 'user');
  },

  async logout() {
    return request(config().apiBase, '/auth/logout', { method: 'POST', body: {} });
  },

  async listUsers() {
    return collection(await request(config().apiBase, '/admin/users'), 'users');
  },

  async createEditor(input) {
    return entity(
      await request(config().apiBase, '/admin/users', { method: 'POST', body: input }),
      'user'
    );
  },

  async listProjects() {
    return collection(await request(config().apiBase, '/projects'), 'projects');
  },

  async createProject(title) {
    return entity(await request(config().apiBase, '/projects', { method: 'POST', body: { title } }), 'project');
  },

  async updateProject(projectId, title) {
    return entity(
      await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}`, {
        method: 'PATCH',
        body: { title },
      }),
      'project'
    );
  },

  async deleteProject(projectId) {
    return request(config().apiBase, `/projects/${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
      body: {},
    });
  },

  async getProject(projectId) {
    return projectPayload(await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}`));
  },

  async saveDraft(projectId, document, expectedRevision, label) {
    return projectPayload(
      await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}/draft`, {
        method: 'PUT',
        body: { document, expectedRevision, label },
      })
    );
  },

  async listVersions(projectId, limit) {
    const query = limit ? `?limit=${encodeURIComponent(limit)}` : '';
    return collection(
      await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}/versions${query}`),
      'versions'
    );
  },

  async createVersion(projectId, label, expectedRevision) {
    return entity(
      await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}/versions`, {
        method: 'POST',
        body: { label, expectedRevision },
      }),
      'version'
    );
  },

  async restoreVersion(projectId, versionId) {
    return projectPayload(
      await request(
        config().apiBase,
        `/projects/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}/restore`,
        { method: 'POST', body: {} }
      )
    );
  },

  async deletePanorama(projectId, panoId, expectedRevision) {
    const payload = await request(
      config().apiBase,
      `/projects/${encodeURIComponent(projectId)}/panos/${encodeURIComponent(panoId)}`,
      { method: 'DELETE', body: { expectedRevision } }
    );
    return {
      ...projectPayload(payload),
      removedPano: payload?.removedPano || null,
      sourceReclaimed: Boolean(payload?.sourceReclaimed),
      sourceRetainedReason: payload?.sourceRetainedReason || null,
    };
  },

  async deleteAllPanoramas(projectId, expectedRevision, confirmed) {
    const payload = await request(
      config().apiBase,
      `/projects/${encodeURIComponent(projectId)}/panos`,
      { method: 'DELETE', body: { expectedRevision, confirmed: confirmed === true } }
    );
    return {
      ...projectPayload(payload),
      removedCount: Number(payload?.removedCount || 0),
      sourceReclaimedCount: Number(payload?.sourceReclaimedCount || 0),
      sourceRetainedCount: Number(payload?.sourceRetainedCount || 0),
      reclaimedBytes: Number(payload?.reclaimedBytes || 0),
    };
  },

  async listReleases(projectId) {
    return collection(
      await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}/releases`),
      'releases'
    );
  },

  async createRelease(projectId, input = {}) {
    const payload = await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}/releases`, {
        method: 'POST',
        body: input,
      });
    return { ...entity(payload, 'release'), publicUrl: payload?.publicUrl || null };
  },

  async updateReleaseNote(projectId, releaseId, note) {
    return entity(
      await request(
        config().apiBase,
        `/projects/${encodeURIComponent(projectId)}/releases/${encodeURIComponent(releaseId)}`,
        { method: 'PATCH', body: { note } }
      ),
      'release'
    );
  },

  async revokeRelease(projectId, releaseId) {
    return entity(
      await request(
        config().apiBase,
        `/projects/${encodeURIComponent(projectId)}/releases/${encodeURIComponent(releaseId)}/revoke`,
        { method: 'POST', body: {} }
      ),
      'release'
    );
  },

  async createUpload(projectId, input) {
    return entity(
      await request(config().apiBase, `/projects/${encodeURIComponent(projectId)}/uploads`, {
        method: 'POST',
        body: input,
      }),
      'upload'
    );
  },

  async getUpload(assetId) {
    return entity(await request(config().apiBase, `/uploads/${encodeURIComponent(assetId)}`), 'upload');
  },

  async signParts(assetId, partNumbers) {
    return request(config().apiBase, `/uploads/${encodeURIComponent(assetId)}/sign-parts`, {
      method: 'POST',
      body: { partNumbers },
    });
  },

  async uploadPart(url, blob, signal) {
    const response = await fetch(endpoint(window.location.origin, url), {
      method: 'PUT',
      body: blob,
      credentials: 'include',
      signal,
      headers: { 'Content-Type': 'application/octet-stream' },
    });
    if (!response.ok) {
      const payload = await parseResponse(response);
      throw new CloudApiError(errorMessage(payload, `上传分片失败（${response.status}）`), {
        status: response.status,
        details: payload?.details || null,
      });
    }
  },

  async completeUpload(assetId) {
    return request(config().apiBase, `/uploads/${encodeURIComponent(assetId)}/complete`, {
      method: 'POST',
      body: {},
    });
  },

  async deleteUpload(assetId) {
    return request(config().apiBase, `/uploads/${encodeURIComponent(assetId)}`, {
      method: 'DELETE',
      body: {},
    });
  },

  async publicManifest(publicId) {
    return request(config().publicApiBase, `/tours/${encodeURIComponent(publicId)}/manifest`);
  },
};

export function signedPartUrl(payload, partNumber, assetId) {
  const entries = payload?.parts || payload?.signedParts || payload?.urls || [];
  const entry = Array.isArray(entries)
    ? entries.find((item) => Number(item?.partNumber || item?.number) === Number(partNumber))
    : null;
  const url = typeof entry === 'string' ? entry : entry?.url || entry?.uploadUrl;
  return url || apiPath(`/uploads/${encodeURIComponent(assetId)}/parts/${partNumber}`);
}
